import { motion } from "framer-motion";
import { ArrowUpRight, Smartphone, Zap, Gamepad2, Wallet } from "lucide-react";
import { useLang } from "./LanguageContext";
import { Reveal } from "./Reveal";
import { IMAGES, TRX_URL } from "./content";

const Card = ({ img, icon: Icon, tag, title, desc, className, testid }) => (
  <a
    href={TRX_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`group relative block overflow-hidden border border-white/10 bg-white/[0.02] hover:border-[#ccff00]/60 transition-colors duration-300 ${className}`}
  >
    <div className="absolute inset-0">
      <img
        src={img}
        alt={title}
        className="h-full w-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black via-black/70 to-transparent" />
    </div>
    <div className="relative z-10 flex h-full flex-col justify-between p-8">
      <div className="flex items-start justify-between">
        <span className="flex h-12 w-12 items-center justify-center border border-white/20 bg-black/40 backdrop-blur-sm text-[#ccff00] group-hover:bg-[#ccff00] group-hover:text-black transition-colors">
          <Icon className="h-5 w-5" />
        </span>
        <span className="font-display font-black text-4xl text-white/10 group-hover:text-[#ccff00]/40 transition-colors">
          {tag}
        </span>
      </div>
      <div>
        <h3 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight mb-3 flex items-center gap-2">
          {title}
          <ArrowUpRight className="h-5 w-5 text-[#ccff00] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
        </h3>
        <p className="max-w-md text-sm md:text-base text-zinc-400 font-light leading-relaxed">{desc}</p>
      </div>
    </div>
  </a>
);

export const Products = () => {
  const { t } = useLang();
  const p = t.products.items;

  return (
    <section id="products" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="max-w-3xl mb-16">
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#ccff00]">
              {t.products.overline}
            </span>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight text-white mt-4 leading-tight">
              {t.products.title}
            </h2>
            <p className="mt-5 text-zinc-400 font-light text-base md:text-lg">{t.products.desc}</p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-7 min-h-[380px] md:min-h-[440px]"
          >
            <Card
              testid="product-pulsa"
              img={IMAGES.pulsa}
              icon={Smartphone}
              tag={p.pulsa.tag}
              title={p.pulsa.title}
              desc={p.pulsa.desc}
              className="h-full"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="md:col-span-5 min-h-[380px] md:min-h-[440px]"
          >
            <Card
              testid="product-pln"
              img={IMAGES.pln}
              icon={Zap}
              tag={p.pln.tag}
              title={p.pln.title}
              desc={p.pln.desc}
              className="h-full"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            className="md:col-span-5 min-h-[380px] md:min-h-[440px]"
          >
            <Card
              testid="product-game"
              img={IMAGES.game}
              icon={Gamepad2}
              tag={p.game.tag}
              title={p.game.title}
              desc={p.game.desc}
              className="h-full"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="md:col-span-7 min-h-[380px] md:min-h-[440px]"
          >
            <Card
              testid="product-wallet"
              img={IMAGES.wallet}
              icon={Wallet}
              tag={p.wallet.tag}
              title={p.wallet.title}
              desc={p.wallet.desc}
              className="h-full"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
